import { Component, EventEmitter, inject, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { DeviceSelectorComponent } from 'src/app/shared/components/device-selector.component';
import { AiRestrictedHoverDirective } from 'src/app/shared/directives/ai-restricted-hover.directive';
import { OVERLAY_DATA } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { environment } from 'src/environments/environment';
import { Command } from '../audio-menus.dto';

@Component({
  selector: 'app-audio-recorder-menu',
  standalone: true,
  styleUrls: ['../../../recorders.scss'],
  imports: [TranslateModule, MatButtonModule, MatIconModule, MatTooltipModule, DeviceSelectorComponent, AiRestrictedHoverDirective],
  template: `
    <div class="custom-context-menu-wrapper d-flex flex-column gap-2 mb-1 p-2">
      <div id="device-selector-wrapper" class="mt-2 mb-1">
        <app-device-selector [kind]="'audioinput'" [label]="'DeviceSelector.MicrophoneLabel'" (selectionChange)="onMicrophoneSelected($event)"> </app-device-selector>
      </div>
      <button mat-flat-button (click)="sendCommand('record-audio')">
        <mat-icon>mic</mat-icon>
        <span>{{ 'AudioPlay.Tooltip.RecordAudio' | translate }}</span>
      </button>
      @if (environment.enableLiveTranscription) {
        <button
          mat-flat-button
          (click)="sendCommand('record-with-transcription')"
          [disabled]="!canUseAI"
          [matTooltip]="!canUseAI ? ('GenerateContent.NoAiAccessTooltip' | translate) : ''"
          [appAiRestrictedHover]="!canUseAI"
        >
          <mat-icon>speech_to_text</mat-icon>
          <span>{{ 'AudioPlay.Tooltip.RecordAndTranscribe' | translate }}</span>
        </button>
      }
      <button
        mat-flat-button
        (click)="sendCommand('transcribe-only')"
        [disabled]="!canUseAI"
        [matTooltip]="!canUseAI ? ('GenerateContent.NoAiAccessTooltip' | translate) : ''"
        [appAiRestrictedHover]="!canUseAI"
      >
        <mat-icon>speech_to_text</mat-icon>
        <span>{{ 'AudioPlay.Tooltip.TranscribeOnly' | translate }}</span>
      </button>
      <button mat-flat-button (click)="sendCommand('add-audio-file')">
        <mat-icon>audio_file</mat-icon>
        <span>{{ 'AudioPlay.Tooltip.AddAudioFile' | translate }}</span>
      </button>
    </div>
  `,
})
export class AudioRecorderMenuComponent {
  @Output() commandSelected = new EventEmitter<Command>();

  public data: {
    hasRecording: boolean;
    isRecording: boolean;
    isTranscribing: boolean;
  } = inject(OVERLAY_DATA);

  private authService = inject(AuthService);
  canUseAI = this.authService.canUseAI();

  selectedMicrophoneId = signal<string | null>(localStorage.getItem('selectedMicrophone'));
  environment = environment;

  onMicrophoneSelected(deviceId: string): void {
    this.selectedMicrophoneId.set(deviceId);
  }

  sendCommand(type: string): void {
    if ((type === 'record-with-transcription' || type === 'transcribe-only') && !this.canUseAI) return;
    const output: Command = {
      type: type,
      data: {},
    };
    this.commandSelected.emit(output);
  }
}
